"use client";

import { useState } from 'react';

export default function ReaderPage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [readText, setReadText] = useState('');
  const [ paused, setPaused ] = useState(false);

  const handleRead = async () => {
    if (!url) return;
    setLoading(true);
    setError('');
    setSpeaking(false);

    try {
      const res = await fetch('/api/read-for-me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      const data = await res.json();
      
      if (res.ok) {
        setReadText(data.text);
        speakText(data.text);
      } else {
        setError(data.error || 'Failed to read page');
      }
    } catch (err) {
      setError('An error occurred. Make sure the URL is valid.');
    } finally {
      setLoading(false);
    }
  };

  const speakText = (text: string) => {
    if (!('speechSynthesis' in window)) {
        alert('Speech synthesis not supported in this browser');
        return;
    }

    // 1. Force clear any glitched or stuck audio queue
    window.speechSynthesis.cancel();

    // 2. Clean up text and split it into sentences to prevent silent memory crashes
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    let currentSentenceIndex = 0;

    const speakSentence = () => {
        if (currentSentenceIndex >= sentences.length) {
        setSpeaking(false);
        return;
        }

        const currentText = sentences[currentSentenceIndex].trim();
        if (!currentText) {
        currentSentenceIndex++;
        speakSentence();
        return;
        }

        const utterance = new SpeechSynthesisUtterance(currentText);
        
        // 3. Explicitly look for and assign an English system voice
        const voices = window.speechSynthesis.getVoices();
        const englishVoice = voices.find(v => v.lang.startsWith('en')) || voices[0];
        if (englishVoice) {
        utterance.voice = englishVoice;
        }

        // Handle lifecycle events
        utterance.onstart = () => {
            setSpeaking(true);
            setPaused(false);
        }

        
        utterance.onend = () => {
        currentSentenceIndex++;
        speakSentence(); // Automatically move to the next sentence
        };

        utterance.onerror = (event) => {
            // console.error("Speech Error:", event);
            // If a chunk errors out, attempt to bypass it and continue
            // currentSentenceIndex++;
            // speakSentence();
            setSpeaking(false);
            return;
        };

        // 4. Wrap execution in a tiny timeout to ensure the browser has fully cleared its previous state
        setTimeout(() => {
        window.speechSynthesis.speak(utterance);
        }, 50);
    };

    // Start the reading sequence
    speakSentence();
    };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
  };

  const pauseSpeaking = () => {
    window.speechSynthesis.pause();
    // setSpeaking(false);
    setPaused(true);
  };

  const resumeSpeaking = () => {
    window.speechSynthesis.resume();
    // setSpeaking(false);
    setPaused(false);
  };

  const handleSpeaking = () => {
    if(paused) resumeSpeaking();
    else pauseSpeaking();
  }

  return (
    <main className="max-w-2xl mx-auto mt-20 p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">URL to Speech Reader</h1>
      
      <div className="flex flex-col gap-4">
        <input
          type="url"
          placeholder="Paste URL here (e.g., https://example.com)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="flex gap-2">
          <button
            onClick={handleRead}
            disabled={loading}
            className="bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-300 flex-1"
          >
            {loading ? 'Fetching Page...' : 'Read Page'}
          </button>
          
          {speaking && (
            <>
                <button
                onClick={stopSpeaking}
                className="bg-red-500 text-white p-3 rounded-lg font-semibold hover:bg-red-600"
                >
                Stop
                </button>
                <button
                    onClick={handleSpeaking}
                    className="bg-red-500 text-white p-3 rounded-lg font-semibold hover:bg-red-600"
                >
                { paused ? "Pause" : "Resume" }
                </button>
            </>
          )}
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        
        {readText && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg overflow-y-auto text-sm text-gray-600 whitespace-pre-line">
                <strong>Extracted Text:</strong> 
                {readText}
            </div>
        )}
      </div>
    </main>
  );
}
